import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Api, Endpoint } from '../../api'
import { toast } from 'react-toastify'

const SquareImportButton = ({
	Restaurant,
	Dish,
	Category,
	showConnectionStatus = false,
	connectionStatus,
	quickEdit,
	onSyncComplete,
}) => {
	const [importing, setImporting] = useState(false)
	const [status, setStatus] = useState(null)
	const [quickEditMode, _] = useState(quickEdit || false)
	const { restaurant } = Restaurant

	useEffect(() => {
		if (connectionStatus?.isConnected !== true) return
		Restaurant?.find?.({ forceRender: true })
	}, [connectionStatus?.isConnected])

	const handleImport = async () => {
		setImporting(true)
		setStatus(null)

		try {
			// Prefer pulling the restaurant id from Redux
			const restaurantId = restaurant?._id
			const options = restaurantId ? { data: { user: { restaurant: restaurantId } } } : {}

			// Use centralized API client (handles auth + JSON)
			const result = await Api.call(Endpoint.square.import, options)

			// Handle new response structure
			if (result?.confirmation === 'Success') {
				const data = result?.data
				setStatus({
					success: true,
					message:
						data?.message ||
						(quickEditMode ? 'Sync completed successfully' : 'Import completed successfully'),
					summary: data?.summary,
				})

				// Reload dishes and categories so tables update automatically
				await Dish?.load?.({ forceRender: true })
				await Category?.load?.({ forceRender: true })
				await Restaurant?.find?.({ forceRender: true })

				// Re-run loadQuickEdit if callback provided (for sync mode)
				if (typeof onSyncComplete === 'function') onSyncComplete()
			} else {
				// Handle error response
				const errorMessage = result?.message || (quickEditMode ? 'Sync failed' : 'Import failed')

				// Check if this was an auto-disconnect (expired tokens)
				const wasDisconnected =
					result?.metadata?.disconnected ||
					errorMessage.includes('expired') ||
					errorMessage.includes('reconnect')

				setStatus({
					error: errorMessage,
					needsReconnect: wasDisconnected,
				})
			}
		} catch (error) {
			const errorMsg = error.message || `An error occurred during ${quickEditMode ? 'sync' : 'import'}`
			setStatus({ error: errorMsg })
		} finally {
			setImporting(false)
		}
	}

	const handleReconnect = async () => {
		try {
			const result = await Api.call(Endpoint.square.authorize)

			if (result?.confirmation === 'Success' && result?.message?.authorizationUrl) {
				// Redirect to Square OAuth
				window.location.href = result.message.authorizationUrl
			} else {
				toast('Failed to initiate Square connection')
			}
		} catch (error) {
			toast('Error connecting to Square: ' + error.message)
		}
	}

	const isConnected = connectionStatus?.isConnected === true
	const showWarning = showConnectionStatus && !isConnected

	return (
		<div className="square-import-section">
			{/* Compact badge for quick edit, full warning for integration page */}
			{showWarning && quickEditMode && (
				<span
					style={{
						fontSize: 12,
						color: '#856404',
						background: '#fff3cd',
						border: '0.5px solid #ffc107',
						borderRadius: 6,
						padding: '4px 10px',
						whiteSpace: 'nowrap',
					}}
				>
					Not connected
				</span>
			)}
			{showWarning && !quickEditMode && (
				<div className="warning-message">
					<p>⚠️ Square is not connected. Please connect your Square account first.</p>
				</div>
			)}

			<div className="button-group">
				<button
					onClick={handleImport}
					disabled={importing || (showConnectionStatus && !isConnected)}
					className="btn btn-primary"
				>
					{importing
						? quickEditMode
							? 'Syncing...'
							: 'Importing...'
						: quickEditMode
							? 'Sync from Square'
							: 'Import from Square'}
				</button>{' '}
			</div>

			{status && !quickEditMode && (
				<div className={`status ${status.error ? 'error' : 'success'}`}>
					{status.error ? (
						<div>
							<p>Error: {status.error}</p>
							{status.needsReconnect && (
								<div className="reconnect-section">
									<p className="reconnect-message">
										Your Square connection has expired. Please reconnect to continue importing.
									</p>
									<button onClick={handleReconnect} className="btn btn-reconnect">
										Reconnect to Square
									</button>
								</div>
							)}
						</div>
					) : (
						<div>
							<p>{status.message}</p>
							{status.summary && (
								<ul>
									<li>Categories: {status.summary.categoriesCreated}</li>
									<li>Dishes: {status.summary.dishesCreated}</li>
									<li>Modifier Lists: {status.summary.modifierListsImported}</li>
									<li>Skipped: {status.summary.itemsSkipped}</li>
									{status.summary.totalErrors > 0 && (
										<li className="error-count">Errors: {status.summary.totalErrors}</li>
									)}
								</ul>
							)}
						</div>
					)}
				</div>
			)}

			<style jsx="true">{`
				.checking-connection {
					padding: 10px;
					background: #e7f3ff;
					border-radius: 4px;
					margin-bottom: 10px;
					color: #004085;
				}

				.warning-message {
					padding: 12px;
					background: #fff3cd;
					border-left: 4px solid #ffc107;
					border-radius: 4px;
					margin-bottom: 10px;
				}

				.warning-message p {
					margin: 0;
					color: #856404;
				}

				.status.error {
					background: #f8d7da;
					border-left: 4px solid #dc3545;
					padding: 12px;
					border-radius: 4px;
					margin-top: 10px;
				}

				.status.success {
					background: #d4edda;
					border-left: 4px solid #28a745;
					padding: 12px;
					border-radius: 4px;
					margin-top: 10px;
				}

				.status ul {
					margin: 10px 0 0 0;
					padding-left: 20px;
				}

				.status li {
					margin: 5px 0;
				}

				.error-count {
					color: #dc3545;
					font-weight: 600;
				}

				.reconnect-section {
					margin-top: 15px;
					padding: 15px;
					background: #fff3cd;
					border-left: 3px solid #ffc107;
					border-radius: 4px;
				}

				.reconnect-message {
					margin: 0 0 10px 0;
					color: #856404;
					font-weight: 500;
				}

				.btn-reconnect {
					background: #007bff;
					color: white;
					border: none;
					padding: 10px 20px;
					border-radius: 4px;
					font-weight: 500;
					cursor: pointer;
					transition: background 0.2s;
				}

				.btn-reconnect:hover {
					background: #0056b3;
				}
			`}</style>
		</div>
	)
}

const mapStateToProps = (state) => ({
	Restaurant: state?.Restaurant,
	Dish: state?.Dish,
	Category: state?.Category,
})
export default connect(mapStateToProps)(SquareImportButton)
